import { InputLabelPositions, StandardSwitch } from '@brandfolder/react';
import { t, Trans } from '@lingui/macro';
import { AssemblyResult as Result } from '@uppy/transloadit';
import { PickerResponse } from 'filestack-js/build/main/lib/picker';
import React, {
  useEffect,
  useRef,
  useState,
  FunctionComponent,
} from 'react';

import { UploaderSources } from '@api/uploaders/UploaderEnums';
import { BrandguideCardImage, updateBrandguide } from '@api/v3/brandguides';
import { getBrandguide } from '@api/v4/resources/brandguides';
import { FilestackPreviewImage } from '@components/common/filestack_uploader/FilestackPreviewImage';
import { FilestackUploader } from '@components/common/filestack_uploader/FilestackUploader';
import { BFLoader } from '@components/common/loader/main';
import { UppyFeatureFlagWrapper } from '@components/common/uppy/UppyFeatureFlagWrapper';
import {
  CloseButton,
  PrimaryButton,
  TextWarningButton,
} from '@components/library/button/index';
import InputContainer from '@components/library/input_container/InputContainer';
import { InputType } from '@components/library/inputs/Input.props';
import renderModal from '@components/show_page/modals/renderModal';
import { resolveCardImage, setPendingCardImage, updateBrandguideCardImage } from '@helpers/brandguide';
import { formattedUppyAttachment, isUppyUploaderEnabled } from '@helpers/uppy';

interface SettingsModalProps {
  brandguideKey: string;
  closeModal: () => void;
  organizationKey: string | null;
}

interface Attributes {
  cardImage: string;
  name: string;
  isPublic: boolean;
  slug: string;
}

const defaultData: Attributes = { cardImage: '', name: '', isPublic: true, slug: '' };

const SettingsModal: FunctionComponent<SettingsModalProps> = ({
  brandguideKey,
  closeModal,
  organizationKey
}) => {
  const [initialData, setInitialData] = useState(defaultData);
  const [updatedData, setUpdatedData] = useState(defaultData);
  const [cardImageUpload, setCardImageUpload] = useState<BrandguideCardImage | null>(null);
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const triggerClickHandlerRef = useRef<((event: React.MouseEvent) => void) | null>(null);
  const uploadAreaId = 'brandguide-card-image-upload';

  const handleSubmit = async (): Promise<void> => {
    const { cardImage, isPublic, name, slug } = updatedData;
    setIsSubmitting(true);

    // Send the metadata captured at upload time so the backend can process the image;
    // fall back to the bare URL when the selection didn't come from this session's uploader.
    const cardImagePayload = cardImageUpload?.url === cardImage
      ? cardImageUpload
      : { url: cardImage };

    const isCardImageUpdated = initialData.cardImage !== cardImage;
    // The card renders these server-side, so only they still need a reload to show up
    const isCardDetailUpdated = initialData.name !== name
      || initialData.isPublic !== isPublic
      || initialData.slug !== slug;

    try {
      await updateBrandguide({
        initialSlug: initialData.slug,
        ...isCardImageUpdated && { cardImage: cardImagePayload },
        ...(initialData.name !== name) && { name },
        ...(initialData.isPublic !== isPublic) && { isPublic },
        ...(initialData.slug !== slug) && { slug },
      });

      if (isCardImageUpdated) {
        setPendingCardImage(brandguideKey, cardImage);
        updateBrandguideCardImage(brandguideKey, cardImage);
      }

      Notify.create({
        title: isCardDetailUpdated
          ? t`Brandguide settings updated! Refreshing...`
          : t`Brandguide settings updated!`,
        type: 'success'
      });
      setIsSubmitDisabled(true);

      if (isCardDetailUpdated) {
        window.location.reload();
        return;
      }

      closeModal();
    } catch (error) {
      Notify.create({
        title: t`An error occurred while updating this Brandguide. Please try again or contact support.`,
        type: 'error',
        ...error.errors && { body: error.errors },
      });
      // Only reset on failure — success either reloads or unmounts the modal
      setIsSubmitting(false);
    }
  };

  const handleCardImageUpload = (file: BrandguideCardImage): void => {
    setCardImageUpload(file);
    setUpdatedData((prevState) => ({ ...prevState, cardImage: file.url }));
  };

  const handleUppyUpload = (files: Result[]): void => {
    if (!files.length) return;

    const [file] = files;
    const { filename, mimetype, source, url } = formattedUppyAttachment(
      file,
      (file as Result & { source?: string })?.source
    ) as BrandguideCardImage;
    // Without a url the submit button would enable on an unsaveable payload
    if (!url) return;

    handleCardImageUpload({ filename, mimetype, source, url });
  };

  const handleFilestackUpload = (files: PickerResponse): void => {
    const file = files?.filesUploaded?.[0];
    if (!file) return;

    handleCardImageUpload({
      filename: file.filename,
      mimetype: file.mimetype,
      source: UploaderSources.Filestack,
      url: file.url,
    });
  };

  const handleRemoveCardImage = (): void => {
    setCardImageUpload(null);
    setUpdatedData((prevState) => ({ ...prevState, cardImage: '' }));
  };

  const handleUpdateInput = (e: InputChangeEvent): void => {
    e.persist();
    const { name, value } = e.target;
    setUpdatedData((prevState) => ({ ...prevState, [name]: value }));
  };

  const checkDiff = (): void => {
    const {
      cardImage: cardImageInitial,
      isPublic: isPublicInitial,
      name: nameInitial,
      slug: slugInitial
    } = initialData;

    const { cardImage, isPublic, name, slug } = updatedData;

    setIsSubmitDisabled(
      cardImageInitial === cardImage &&
      isPublic === isPublicInitial &&
      name === nameInitial &&
      slug === slugInitial
    );
  };

  const fetchBrandguide = async (): Promise<void> => {
    try {
      const { attributes } = await getBrandguide(brandguideKey);
      const { card_image: savedCardImage, name, public: isPublic, slug } = attributes;
      const cardImage = resolveCardImage(brandguideKey, savedCardImage);
      setInitialData({ cardImage, name, isPublic, slug });
      setUpdatedData({ cardImage, name, isPublic, slug });
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchBrandguide();
    return closeModal;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    checkDiff();
  }, [updatedData]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="modal-content-wrapper brandguide-modal">
      <div className="modal-content-wrapper__header">
        <span className="bff-settings icon" />
        <h3><Trans>Brandguide Settings</Trans></h3>
        <CloseButton onClick={closeModal} />
      </div>
      {isLoading ? (
        <div className="loader-container"><BFLoader /></div>
      ) : (
        <div className="modal-content-wrapper__body">
          <InputContainer
            attributes={{
              name: 'name',
              onChange: handleUpdateInput,
              type: InputType.Text,
            }}
            input={{ value: updatedData?.name }}
            labelCopy={t`Name`}
          />
          <InputContainer
            attributes={{
              name: 'slug',
              onChange: handleUpdateInput,
              type: InputType.Text,
            }}
            input={{ value: updatedData?.slug }}
            labelCopy={t`Unique URL`}
          />
          <StandardSwitch
            className="brandguide-modal-switch"
            id="brandguide-is-public"
            isChecked={updatedData.isPublic}
            labelPosition={InputLabelPositions.Right}
            onChange={(): void => {
              setUpdatedData((prevState) => ({ ...prevState, isPublic: !prevState.isPublic }));
            }}
          >
            <Trans>Public</Trans>
          </StandardSwitch>
          <div className="brandguide-modal__filestack-container">
            <div className="brandguide-modal__filestack-container--description">
              <Trans>
                <h3>Card Image</h3>
                <p>Recommended size: 260px x 130px</p>
              </Trans>
            </div>
            {updatedData?.cardImage ? (
              <div className="brandguide-modal__filestack-container--thumbnail">
                <div className="brandguide-modal__filestack-container--thumbnail-container">
                  <FilestackPreviewImage
                    alt="thumbnail"
                    className="brandguide-modal__filestack-container--thumbnail--image"
                    resourceKey={organizationKey || undefined}
                    resourceType={organizationKey ? 'organization' : undefined}
                    src={updatedData?.cardImage}
                  />
                </div>
                <TextWarningButton
                  icon="bff-trash"
                  onClick={handleRemoveCardImage}
                />
              </div>
            ) : (
              isUppyUploaderEnabled() ? (
                <>
                  <div className="filestack-uploader-component">
                    <div className="filestack-upload-area-wrapper">
                      <div
                        className="filestack-upload-area companion-upload-trigger"
                        id={uploadAreaId}
                        onClick={(e): void => { if (triggerClickHandlerRef.current) triggerClickHandlerRef.current(e); }}
                        onKeyUp={(e): void => {
                          if (triggerClickHandlerRef.current) {
                            triggerClickHandlerRef.current(e as unknown as React.MouseEvent);
                          }
                        }}
                        role="button"
                        tabIndex={0}
                      >
                        <div className="fsp-drop-pane__container">
                          <div className="fsp-drop-pane__text">
                            <Trans>Drag file(s) here or click to browse</Trans>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* Both flags / uploader-only → Transloadit; companion-only → Companion */}
                  <UppyFeatureFlagWrapper
                    button
                    cropperOptions={{ aspectRatio: 260 / 130, croppedCanvasOptions: {} }}
                    handleUpload={handleUppyUpload}
                    onTriggerClickHandler={triggerClickHandlerRef}
                    restrictions={{ maxNumberOfFiles: 1, allowedFileTypes: ['image/*'] }}
                    trigger={`#${uploadAreaId}`}
                    uniqIdentifier="uppy-brandguide-card-image"
                  />
                </>
              ) : (
                <FilestackUploader
                  onUploadDone={handleFilestackUpload}
                  pickerOptions={{
                    accept: 'image/*',
                    maxFiles: 1,
                    transformations: {
                      crop: {
                        aspectRatio: (260 / 130),
                        force: true
                      }
                    }
                  }}
                  resourceKey={organizationKey || undefined}
                  resourceType={organizationKey ? 'organization' : undefined}
                />
              )
            )}
          </div>
          <div className="button-container">
            <PrimaryButton
              disabled={isSubmitDisabled}
              isLoading={isSubmitting}
              onClick={(): void => {
                handleSubmit();
              }}
              size="medium"
            >
              <Trans>Update Settings</Trans>
            </PrimaryButton>
          </div>
        </div>
      )}
    </div>
  );
};

const ModalComponent = renderModal(SettingsModal, 'brandguide-settings-modal');
export default ModalComponent;
